import axios from 'utils/axios';
import type { LeadCloudFileType } from '@econtractor/types';

export interface CloudUploadFileMeta {
    fileName: string;
    contentType: string;
    fileSize: number;
}

export interface CloudUploadScope {
    eventId?: number;
    leadId?: number;
    productId?: number;
    serviceId?: number;
    objectId?: number;
    paymentId?: number;
    avatarId?: number;
    taskId?: number;
}

export interface CloudUploadOptions {
    keyPrefix?: string;
}

export const getCloudflareUploadUrls = async (
    files: CloudUploadFileMeta[],
    scope: CloudUploadScope = {},
    options: CloudUploadOptions = {},
): Promise<LeadCloudFileType[] | null> => {
    try {
        const response = await axios.post('/files/cloudflare/upload-urls', {
            files,
            scope,
            keyPrefix: options?.keyPrefix || '',
        });
        if (!response?.data?.stat) return null;
        return response?.data?.files || [];
    } catch (e) {
        console.error(e);
        return null;
    }
};

export const linkCloudflareFiles = async (files: LeadCloudFileType[], scope: CloudUploadScope = {}): Promise<boolean> => {
    try {
        const response = await axios.post('/files/cloudflare/link-files', { files, scope });
        return response?.data?.stat || false;
    } catch (e) {
        console.error(e);
        return false;
    }
};

export const deleteCloudflareFiles = async (
    data: {
        storageKeys?: string[];
        publicUrls?: string[];
        scope?: CloudUploadScope;
    },
): Promise<boolean> => {
    try {
        const response = await axios.post('/files/cloudflare/delete-files', {
            storageKeys: data?.storageKeys || [],
            publicUrls: data?.publicUrls || [],
            scope: data?.scope || {},
        });
        return response?.data?.stat || false;
    } catch (e) {
        console.error(e);
        return false;
    }
};

/** Fetch Cloudflare public file via API proxy (bypasses browser CORS for PDF embedding). */
export const fetchCloudflareContentBlob = async (publicUrl: string): Promise<Blob | null> => {
    const url = String(publicUrl || '')
        .trim()
        .replace(/&amp;/g, '&');
    if (!url) return null;
    try {
        // POST keeps long/encoded CDN URLs out of query strings (nginx/proxy-safe).
        const response = await axios.post(
            '/files/cloudflare/content',
            { url },
            { responseType: 'blob' },
        );
        const blob = response?.data as Blob | undefined;
        if (!blob || !(blob instanceof Blob) || !blob.size) return null;
        const type = String(blob.type || '').toLowerCase();
        // Auth/error pages may arrive as JSON/HTML blobs when interceptor does not throw.
        if (type.includes('application/json') || type.includes('text/html') || type.includes('text/plain')) {
            return null;
        }
        return blob;
    } catch (e) {
        console.error('[fetchCloudflareContentBlob]', url, e);
        return null;
    }
};
